import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { enGB } from 'date-fns/locale';
import { useMutation } from '@tanstack/react-query';
import {
  downloadActivityCsv,
  type ActivityIncludeMode,
} from '../api/activityApi';
import { useActivitySites } from '../hooks/useActivitySites';

interface ExportActivityModalProps {
  open: boolean;
  onClose: () => void;
  from: string;
  to: string;
  siteIds: number[];
  type: '' | 'Labour' | 'Variation';
}

export function ExportActivityModal({
  open,
  onClose,
  from,
  to,
  siteIds,
  type,
}: ExportActivityModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: sites = [], isLoading: sitesLoading } = useActivitySites();
  const [exportFrom, setExportFrom] = useState<string | null>(from);
  const [exportTo, setExportTo] = useState<string | null>(to);
  const [exportSiteIds, setExportSiteIds] = useState<number[]>(siteIds);
  const [mode, setMode] = useState<ActivityIncludeMode>('booked-approved');

  // Seed the modal from the current page filters each time it opens, so it
  // reflects the latest date window and site selection rather than stale state.
  useEffect(() => {
    if (open) {
      setExportFrom(from);
      setExportTo(to);
      setExportSiteIds(siteIds);
    }
  }, [open, from, to, siteIds]);

  const typeSummary =
    type === ''
      ? 'Labour + Variations'
      : type === 'Labour'
        ? 'Labour only'
        : 'Variations only';

  const exportMutation = useMutation({
    mutationFn: downloadActivityCsv,
    onSuccess: onClose,
  });

  const canExport =
    Boolean(exportFrom) && Boolean(exportTo) && !exportMutation.isPending;

  const handleExport = () => {
    if (!exportFrom || !exportTo) {
      return;
    }
    exportMutation.mutate({
      from: exportFrom,
      to: exportTo,
      siteIds: exportSiteIds,
      type,
      include: mode,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      data-testid="activity-export-modal"
    >
      <DialogTitle>Export activity to CSV</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Stack spacing={3} sx={{ mt: 0.5 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Date range
              </Typography>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  label="From"
                  value={exportFrom ? new Date(exportFrom) : null}
                  onChange={d => setExportFrom(d ? d.toISOString() : null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
                <DatePicker
                  label="To"
                  value={exportTo ? new Date(exportTo) : null}
                  onChange={d => setExportTo(d ? d.toISOString() : null)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Sites
              </Typography>
              <Select
                multiple
                size="small"
                fullWidth
                displayEmpty
                disabled={sites.length === 0}
                value={exportSiteIds}
                onChange={e => setExportSiteIds(e.target.value as number[])}
                input={<OutlinedInput />}
                renderValue={selected => {
                  const ids = selected as number[];
                  if (ids.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        {sitesLoading
                          ? 'Loading sites…'
                          : sites.length === 0
                            ? 'No sites available'
                            : 'All sites'}
                      </Typography>
                    );
                  }
                  if (ids.length === 1) {
                    return sites.find(s => s.id === ids[0])?.name ?? '1 site';
                  }
                  return `${ids.length} sites`;
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
                data-testid="activity-export-site-select"
              >
                {sites.map(site => (
                  <MenuItem key={site.id} value={site.id} sx={{ py: 0.25 }}>
                    <Checkbox
                      size="small"
                      checked={exportSiteIds.includes(site.id)}
                    />
                    <ListItemText primary={site.name} />
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <FormControl>
              <Typography variant="subtitle2" gutterBottom>
                Include
              </Typography>
              <RadioGroup
                value={mode}
                onChange={(_, value) => setMode(value as ActivityIncludeMode)}
              >
                <FormControlLabel
                  value="booked-approved"
                  control={<Radio size="small" />}
                  label="Booked & approved"
                />
                <FormControlLabel
                  value="approved"
                  control={<Radio size="small" />}
                  label="Approved only"
                />
              </RadioGroup>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Work type: {typeSummary}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rejected and cancelled work is never exported.
              </Typography>
            </Box>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={!canExport}
          data-testid="activity-export-confirm"
        >
          {exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
